import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { format } from "date-fns";
import { fi } from "date-fns/locale";
import { useApiUsageLogs, ApiUsageLog } from "@/hooks/useApiUsage";
import { formatCurrency } from "@/lib/formatters";

const PAGE_SIZE = 20;

const STATUS_VARIANTS: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
  success: "default",
  pending: "secondary",
  failed: "destructive",
  rate_limited: "outline",
};

const STATUS_LABELS: Record<string, string> = {
  success: "Onnistui",
  pending: "Käsittelyssä",
  failed: "Epäonnistui",
  rate_limited: "Estetty",
};

const SERVICE_LABELS: Record<string, string> = {
  resend: "Resend",
  "46elks": "46elks",
};

export function UsageLogTable() {
  const [service, setService] = useState<string | undefined>();
  const [status, setStatus] = useState<string | undefined>();
  const [page, setPage] = useState(0);

  const { data: logs, isLoading } = useApiUsageLogs({
    service,
    status,
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
  });

  const hasMore = logs && logs.length === PAGE_SIZE;
  const hasPrev = page > 0;

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <CardTitle>Tapahtumaloki</CardTitle>
          <div className="flex gap-2">
            <Select
              value={service || "all"}
              onValueChange={(v) => {
                setService(v === "all" ? undefined : v);
                setPage(0);
              }}
            >
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="Palvelu" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Kaikki palvelut</SelectItem>
                <SelectItem value="resend">Sähköposti</SelectItem>
                <SelectItem value="46elks">SMS</SelectItem>
              </SelectContent>
            </Select>
            <Select
              value={status || "all"}
              onValueChange={(v) => {
                setStatus(v === "all" ? undefined : v);
                setPage(0);
              }}
            >
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Kaikki</SelectItem>
                <SelectItem value="success">Onnistuneet</SelectItem>
                <SelectItem value="failed">Epäonnistuneet</SelectItem>
                <SelectItem value="rate_limited">Estetyt</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="animate-pulse space-y-2">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-12 bg-muted rounded" />
            ))}
          </div>
        ) : !logs || logs.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            Ei lokimerkintöjä
          </div>
        ) : (
          <>
            <div className="rounded-md border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Aika</TableHead>
                    <TableHead>Palvelu</TableHead>
                    <TableHead>Funktio</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Kustannus</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logs.map((log) => (
                    <TableRow key={log.id}>
                      <TableCell className="whitespace-nowrap">
                        {format(new Date(log.created_at), "d.M.yyyy HH:mm", {
                          locale: fi,
                        })}
                      </TableCell>
                      <TableCell>
                        {SERVICE_LABELS[log.service] || log.service}
                      </TableCell>
                      <TableCell className="font-mono text-xs">
                        {log.function_name}
                      </TableCell>
                      <TableCell>
                        <Badge variant={STATUS_VARIANTS[log.status] || "secondary"}>
                          {STATUS_LABELS[log.status] || log.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        {log.cost_estimate
                          ? formatCurrency(log.cost_estimate)
                          : "-"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between mt-4">
              <p className="text-sm text-muted-foreground">
                Sivu {page + 1}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => p - 1)}
                  disabled={!hasPrev}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Edellinen
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => p + 1)}
                  disabled={!hasMore}
                >
                  Seuraava
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
